import { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import api, { getToken, clearToken } from "@/lib/api";
import { TenantContext } from "@/lib/tenant";
import PinLogin from "@/components/PinLogin";
import OfflineBar from "@/components/OfflineBar";
import { setSyncTenant } from "@/lib/offline";
import {
  SquaresFour,
  CalendarCheck,
  UsersThree,
  Wallet,
  ChartBar,
  Gear,
  HardHat,
  Plant,
  TreePalm,
  SignOut,
  Lock,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

import Dashboard from "@/pages/Dashboard";
import Attendance from "@/pages/Attendance";
import Employees from "@/pages/Employees";
import WageRates from "@/pages/ShiftsWages";
import Payments from "@/pages/Payments";
import Reports from "@/pages/Reports";

import Farmers from "@/pages/Farmers";
import Dehusk from "@/pages/Dehusk";


const MODULES = {

  labour: {

    label: "Labour",

    icon: HardHat,

    accent: "bg-amber-400",

    nav: [

      {
        key: "dashboard",
        label: "Dashboard",
        icon: SquaresFour,
        comp: Dashboard,
      },

      {
        key: "attendance",
        label: "Attendance",
        icon: CalendarCheck,
        comp: Attendance,
      },

      {
        key: "employees",
        label: "Labourers",
        icon: UsersThree,
        comp: Employees,
      },

      {
        key: "payments",
        label: "Payments",
        icon: Wallet,
        comp: Payments,
      },

      {
        key: "shifts",
        label: "Shifts & Wages",
        icon: Gear,
        comp: WageRates,
      },

      {
        key: "reports",
        label: "Reports",
        icon: ChartBar,
        comp: Reports,
      },

    ],

  },


  arecanut: {

    label: "Arecanut",

    icon: TreePalm,

    accent: "bg-emerald-500",

    nav: [

      {
        key: "farmers",
        label: "Farmers",
        icon: Plant,
        comp: Farmers,
      },

      {
        key: "dehusk",
        label: "Dehusk",
        icon: HardHat,
        comp: Dehusk,
      },

    ],

  },

};


// Remembers the last module/page across refreshes. Only a genuinely
// first-ever open (nothing in localStorage yet) falls back to
// Labour -> Attendance; every reload after that restores where the
// user actually was. (Still re-validated against the current tenant's
// enabled_pages once that loads -- see the clamping effect in App().)
const LAST_VIEW_KEY = "agri_last_view";

function loadLastView() {
  try {
    const raw = localStorage.getItem(LAST_VIEW_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (
      v?.moduleKey && MODULES[v.moduleKey] &&
      v?.active && MODULES[v.moduleKey].nav.some((n) => n.key === v.active)
    ) {
      return v;
    }
  } catch {
    // ignore corrupt/unavailable storage
  }
  return null;
}

// Multi-tenant module/page gating: each tenant's `enabled_pages` (from
// /auth/verify) is a flat list of nav `key`s -- e.g. a Farmers-only
// business gets `["farmers"]`, an Attendance-only one gets
// `["attendance"]`. Trims MODULES down to just those pages; a module left
// with zero pages is dropped from the switcher entirely.
function buildModules(enabledPages) {
  const pages = enabledPages || [];
  const out = {};
  for (const [key, mod] of Object.entries(MODULES)) {
    const nav = mod.nav.filter((n) => pages.includes(n.key));
    if (nav.length > 0) out[key] = { ...mod, nav };
  }
  return out;
}


// Mandatory first-login gate: a tenant created by the admin gets handed a
// PIN someone else chose. This blocks the whole app (no Cancel/skip, no
// dismissable backdrop) until they set one only they know -- reuses the
// same POST /auth/change-pin endpoint as the optional ChangePinDialog
// below, just presented full-screen and un-skippable.
function ForceChangePinScreen({ onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!/^\d{4,6}$/.test(next)) {
      toast.error("New PIN must be 4–6 digits");
      return;
    }
    if (next !== confirm) {
      toast.error("New PIN and confirmation don't match");
      return;
    }
    if (next === current) {
      toast.error("Choose a PIN different from the one you were given");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-pin", { current_pin: current, new_pin: next });
      toast.success("PIN updated");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change PIN");
    } finally {
      setLoading(false);
    }
  };

  const digitsOnly = (v) => v.replace(/\D/g, "").slice(0, 6);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-xs">
        <div className="flex flex-col items-center mb-8">
          <div className="h-14 w-14 rounded-xl bg-amber-400 flex items-center justify-center mb-3">
            <Lock size={26} weight="bold" className="text-slate-900" />
          </div>
          <h1 className="text-white text-xl font-semibold tracking-tight">Set Your Own PIN</h1>
          <p className="text-slate-400 text-sm mt-2 text-center">
            You logged in with the PIN your admin set for you. Choose a new one only you know before continuing.
          </p>
        </div>

        <div className="space-y-3">
          <input
            data-testid="force-current-pin"
            type="password"
            inputMode="numeric"
            maxLength={6}
            autoFocus
            value={current}
            onChange={(e) => setCurrent(digitsOnly(e.target.value))}
            placeholder="Current PIN (given by admin)"
            disabled={loading}
            className="w-full h-11 px-4 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm font-data placeholder:text-slate-500 placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <input
            data-testid="force-new-pin"
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={next}
            onChange={(e) => setNext(digitsOnly(e.target.value))}
            placeholder="New PIN (4–6 digits)"
            disabled={loading}
            className="w-full h-11 px-4 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm font-data placeholder:text-slate-500 placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <input
            data-testid="force-confirm-pin"
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={confirm}
            onChange={(e) => setConfirm(digitsOnly(e.target.value))}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            placeholder="Confirm new PIN"
            disabled={loading}
            className="w-full h-11 px-4 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm font-data placeholder:text-slate-500 placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>

        <button data-testid="btn-force-change-pin" disabled={loading} onClick={save}
          className="w-full mt-4 h-12 rounded-xl bg-amber-400 text-slate-900 font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-500 transition-colors">
          {loading ? "Saving…" : "Set PIN & Continue"}
        </button>
      </div>
    </div>
  );
}


function ChangePinDialog({
  open,
  onOpenChange,
}) {

  const [current, setCurrent] =
    useState("");

  const [next, setNext] =
    useState("");


  const save = async () => {

    if (
      !/^\d{4,6}$/.test(
        next
      )
    ) {

      toast.error(
        "New PIN must be 4–6 digits"
      );

      return;

    }


    try {

      await api.post(
        "/auth/change-pin",
        {
          current_pin:
            current,

          new_pin:
            next,
        }
      );


      toast.success(
        "PIN updated"
      );


      setCurrent("");
      setNext("");

      onOpenChange(false);

    } catch (e) {

      toast.error(
        e?.response?.data?.detail ||
          "Could not change PIN"
      );

    }

  };


  return (

    <Dialog
      open={open}
      onOpenChange={
        onOpenChange
      }
    >

      <DialogContent
        className="sm:max-w-[380px]"
      >

        <DialogHeader>

          <DialogTitle>
            Change PIN
          </DialogTitle>

        </DialogHeader>


        <div className="space-y-4 py-2">

          <div className="space-y-1.5">

            <Label>
              Current PIN
            </Label>

            <Input
              data-testid="current-pin"
              type="password"
              inputMode="numeric"
              className="font-data"
              value={current}
              onChange={(e) =>
                setCurrent(
                  e.target.value.replace(
                    /\D/g,
                    ""
                  )
                )
              }
              maxLength={6}
            />

          </div>


          <div className="space-y-1.5">

            <Label>
              New PIN (4–6 digits)
            </Label>

            <Input
              data-testid="new-pin"
              type="password"
              inputMode="numeric"
              className="font-data"
              value={next}
              onChange={(e) =>
                setNext(
                  e.target.value.replace(
                    /\D/g,
                    ""
                  )
                )
              }
              maxLength={6}
            />

          </div>

        </div>


        <DialogFooter>

          <Button
            variant="outline"
            onClick={() =>
              onOpenChange(false)
            }
          >
            Cancel
          </Button>


          <Button
            data-testid="btn-save-pin"
            className="bg-slate-900"
            onClick={save}
          >
            Update PIN
          </Button>

        </DialogFooter>

      </DialogContent>

    </Dialog>

  );

}


function App() {

  const [authed, setAuthed] =
    useState(null);

  const [tenant, setTenant] =
    useState(null);

  /*
   * IMPORTANT:
   *
   * Only a genuinely first-ever open (no remembered view in
   * localStorage) starts in Labour → Attendance. Every refresh after
   * that restores whatever module/page was last open — see
   * loadLastView()/LAST_VIEW_KEY above. Whatever this resolves to gets
   * re-validated against the tenant's enabled_pages once that loads
   * (see the clamping effect below) — a stale/foreign value here never
   * survives long enough to render.
   */
  const [moduleKey, setModuleKey] =
    useState(() => loadLastView()?.moduleKey || "labour");

  const [active, setActive] =
    useState(() => loadLastView()?.active || "attendance");

  const [pinOpen, setPinOpen] =
    useState(false);


  useEffect(() => {
    try {
      localStorage.setItem(LAST_VIEW_KEY, JSON.stringify({ moduleKey, active }));
    } catch {
      // ignore unavailable storage (e.g. private browsing)
    }
  }, [moduleKey, active]);


  const loadSession = () => {
    return api.get("/auth/verify")
      .then((r) => {
        setSyncTenant(r.data.tenant?.slug);
        setTenant(r.data.tenant);
        setAuthed(true);
      })
      .catch(() => {
        setTenant(null);
        setAuthed(false);
      });
  };


  useEffect(() => {

    const onUnauth = () =>
      setAuthed(false);


    window.addEventListener(
      "agri-unauth",
      onUnauth
    );


    if (!getToken()) {

      setAuthed(false);

    } else {

      loadSession();

    }


    return () =>
      window.removeEventListener(
        "agri-unauth",
        onUnauth
      );

  }, []);


  // Modules/pages this tenant actually has -- MODULES trimmed to their
  // enabled_pages. Once it loads, snap moduleKey/active back onto
  // something this tenant can actually see (handles a stale
  // localStorage value from before enabled_pages changed, or from a
  // different tenant on a shared browser).
  const mods = tenant ? buildModules(tenant.enabled_pages) : null;
  const modKeys = mods ? Object.keys(mods) : [];

  useEffect(() => {
    if (!mods || modKeys.length === 0) return;
    if (!mods[moduleKey]) {
      const firstKey = modKeys[0];
      setModuleKey(firstKey);
      setActive(mods[firstKey].nav[0].key);
    } else if (!mods[moduleKey].nav.some((n) => n.key === active)) {
      setActive(mods[moduleKey].nav[0].key);
    }
  }, [tenant]);


  const logout = () => {

    clearToken();

    setAuthed(false);
    setTenant(null);

  };


  if (authed === null || (authed && !tenant)) {

    return (

      <div className="h-screen w-full flex items-center justify-center bg-slate-900 text-slate-400 font-data text-sm">

        Loading…

      </div>

    );

  }


  if (!authed) {

    return (

      <>
        <PinLogin
          onSuccess={loadSession}
        />

        <Toaster
          position="top-right"
          richColors
        />

      </>

    );

  }


  if (tenant.must_change_pin) {

    return (

      <>
        <ForceChangePinScreen
          onDone={loadSession}
        />

        <Toaster
          position="top-right"
          richColors
        />

      </>

    );

  }


  if (modKeys.length === 0) {

    return (

      <div className="h-screen w-full flex flex-col items-center justify-center gap-3 bg-slate-900 text-slate-400 font-data text-sm px-6 text-center">

        <p>This account has no pages enabled yet.</p>
        <p className="text-slate-500 text-xs">Contact the app owner to enable at least one module.</p>

        <button
          data-testid="btn-logout-no-pages"
          onClick={logout}
          className="mt-2 text-amber-400 hover:text-amber-300"
        >
          Log out
        </button>

      </div>

    );

  }


  const mod =
    mods[
      moduleKey
    ] || mods[modKeys[0]];


  const current =
    mod.nav.find(
      (n) =>
        n.key === active
    ) ||
    mod.nav[0];


  const Current =
    current.comp;


  const switchModule = (
    k
  ) => {

    setModuleKey(k);

    setActive(
      mods[k]
        .nav[0]
        .key
    );

  };


  return (

    <TenantContext.Provider value={{ ...tenant, _refresh: loadSession }}>
    <div className="flex h-screen w-full bg-slate-50 overflow-hidden text-slate-900">


      {/* =========================================================
          DESKTOP SIDEBAR
          ========================================================= */}

      <aside className="w-60 flex-shrink-0 hidden md:flex flex-col bg-slate-900 text-slate-300">


        {/* HEADER */}

        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-800">

          <img
            src="/logo/logo.png"
            alt="AgriLedger"
            className="h-9 w-9 rounded-md"
          />


          <div className="leading-tight">

            <div className="text-white font-semibold text-sm tracking-tight">
              AgriLedger
            </div>

            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              {mod.label} module
            </div>

          </div>

        </div>


        {/* MODULE SWITCHER — hidden when this tenant only has one module */}

        {modKeys.length > 1 && (
        <div className="p-3">

          <div className="grid grid-cols-2 gap-1 bg-slate-800 rounded-md p-1">

            {Object.entries(
              mods
            ).map(
              ([k, m]) => (

                <button
                  key={k}
                  data-testid={`module-${k}`}
                  onClick={() =>
                    switchModule(k)
                  }
                  className={`flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-semibold transition-colors ${
                    moduleKey === k
                      ? "bg-white text-slate-900"
                      : "text-slate-400 hover:text-white"
                  }`}
                >

                  <m.icon
                    size={14}
                    weight="bold"
                  />

                  {m.label}

                </button>

              )
            )}

          </div>

        </div>
        )}


        {/* NAVIGATION */}

        <nav className="flex-1 p-3 space-y-1">

          {mod.nav.map(
            (n) => {

              const Icon =
                n.icon;

              const isActive =
                active ===
                n.key;


              return (

                <button
                  key={n.key}
                  data-testid={`nav-${n.key}`}
                  onClick={() =>
                    setActive(
                      n.key
                    )
                  }
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                    isActive
                      ? "bg-slate-800 text-white"
                      : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
                  }`}
                >

                  <Icon
                    size={20}
                    weight={
                      isActive
                        ? "fill"
                        : "regular"
                    }
                  />

                  {n.label}

                </button>

              );

            }
          )}

        </nav>


        {/* BOTTOM ACTIONS */}

        <div className="p-3 border-t border-slate-800 space-y-1">

          <button
            data-testid="btn-change-pin"
            onClick={() =>
              setPinOpen(true)
            }
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-400 hover:bg-slate-800 hover:text-white"
          >

            <Lock
              size={18}
            />

            Change PIN

          </button>


          <button
            data-testid="btn-logout"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-400 hover:bg-slate-800 hover:text-white"
          >

            <SignOut
              size={18}
            />

            Log out

          </button>

        </div>

      </aside>


      {/* =========================================================
          MOBILE TOP BAR
          ========================================================= */}

      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-slate-900 text-white">


        {/* TOP ROW */}

        <div className="flex items-center justify-between px-4 h-14">

          <div className="flex items-center gap-2 font-semibold text-sm">

            <img
              src="/logo/logo.png"
              alt="AgriLedger"
              className="h-7 w-7 rounded"
            />

            AgriLedger

          </div>


          <div className="flex items-center gap-2">

            {modKeys.length > 1 && (
            <div className="flex gap-1 bg-slate-800 rounded-md p-0.5">

              {Object.entries(
                mods
              ).map(
                ([k, m]) => (

                  <button
                    key={k}
                    data-testid={`module-m-${k}`}
                    onClick={() =>
                      switchModule(k)
                    }
                    className={`px-2.5 py-1 rounded text-xs font-semibold ${
                      moduleKey === k
                        ? "bg-white text-slate-900"
                        : "text-slate-400"
                    }`}
                  >

                    {m.label}

                  </button>

                )
              )}

            </div>
            )}


            <button
              data-testid="btn-logout-m"
              onClick={logout}
              className="text-slate-300 p-1"
            >

              <SignOut
                size={18}
              />

            </button>

          </div>

        </div>


        {/* MOBILE NAV */}

        <div className="flex overflow-x-auto no-scrollbar border-t border-slate-800">

          {mod.nav.map(
            (n) => (

              <button
                key={n.key}
                data-testid={`nav-m-${n.key}`}
                onClick={() =>
                  setActive(
                    n.key
                  )
                }
                className={`px-3 py-2 text-xs whitespace-nowrap ${
                  active === n.key
                    ? "text-white border-b-2 border-amber-400"
                    : "text-slate-400"
                }`}
              >

                {n.label}

              </button>

            )
          )}

        </div>

      </div>


      {/* =========================================================
          MAIN CONTENT
          ========================================================= */}

      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto pt-24 md:pt-0">

        <OfflineBar tenantSlug={tenant.slug} />

        <Current />

      </main>


      {/* CHANGE PIN */}

      <ChangePinDialog
        open={pinOpen}
        onOpenChange={
          setPinOpen
        }
      />


      <Toaster
        position="top-right"
        richColors
      />

    </div>
    </TenantContext.Provider>

  );

}


export default App;